import { forwardRef } from "react";
import { Control, Controller, FieldValues, Path, RegisterOptions } from "react-hook-form";
import styled from "@emotion/styled";

import { combineRefs } from "./util";

export type InputProps<TFieldValues extends FieldValues = FieldValues> =
  React.ComponentPropsWithoutRef<"input"> & {
    name: Path<TFieldValues>;
    control?: Control<TFieldValues>;
    rules?: Omit<
      RegisterOptions<TFieldValues>,
      "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
    >;
  };

export type InputCoerced = <TFieldValues extends FieldValues = FieldValues>(
  props: InputProps<TFieldValues> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => React.ReactElement;

/**
 * Input component with consistent styling and states
 * @param type - The input type (text, password, email, etc.)
 * @param control - React Hook Form control object for form integration
 * @param name - Field name for React Hook Form
 * @param props - All other standard input props
 */
const Input = forwardRef(function Input<TFieldValues extends FieldValues = FieldValues>(
  { type = "text", control, name, rules, ...props }: InputProps<TFieldValues>,
  externalRef: React.ForwardedRef<HTMLInputElement>,
) {
  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <StyledInput
            id={name}
            {...props}
            {...field}
            type={type}
            ref={combineRefs(field.ref, externalRef)}
            onBlur={(e) => {
              field.onBlur?.();
              props.onBlur?.(e);
            }}
            onChange={(e) => {
              field.onChange?.(e);
              props.onChange?.(e);
            }}
          />
        )}
      />
    );
  }
  return <StyledInput id={name} ref={externalRef} type={type} name={name} {...props} />;
}) as InputCoerced; // Needed for generics and useForm type error

export const StyledInput = styled.input(({ theme }) => ({
  // Base styles matching Figma design
  height: 32,
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
  padding: "5px 8px",
  fontSize: 14,
  lineHeight: "157%",
  outline: "none",
  transition: "border-color 0.2s, background-color 0.2s, color 0.2s, outline 0.2s",
  color: theme.colors.sysTextBlack,
  boxSizing: "border-box",

  "&::placeholder": {
    color: theme.colors.sysTextInactive,
  },

  "&:hover:not(:disabled):not(:user-invalid)": {
    borderColor: theme.colors.sysPrimaryDefault,
  },

  "&:focus": {
    borderColor: theme.colors.sysPrimaryDefault,
    outline: `2px solid ${theme.colors.sysPrimaryDefault}1A`,
  },

  // Invalid/error state
  "&:user-invalid": {
    borderColor: theme.colors.sysDestructiveDefault,

    "&:focus": {
      borderColor: theme.colors.sysDestructiveDefault,
      outline: `2px solid ${theme.colors.sysDestructiveDefault}1A`,
    },
  },
  "&:disabled": {
    backgroundColor: theme.colors.sysBackgroundInactive,
    color: theme.colors.sysTextInactive,
    borderColor: theme.colors.sysBackgroundInactive,
    cursor: "not-allowed",
  },
}));

export default Input;
